import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Upload, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ImportHistory() {
    const [items, setItems] = useState([]);
    const navigate = useNavigate();
    useEffect(() => {
        api.get("/imports").then(r => setItems(r.data));
    }, []);
    return (
        <div className="space-y-6" data-testid="imports-history-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Imports</h1>
                    <p className="text-sm text-muted-foreground mt-1">Every upload, every batch.</p>
                </div>
                <Link to="/imports/new">
                    <Button size="sm" data-testid="new-import-button">
                        <Plus className="h-4 w-4 mr-1" /> New import
                    </Button>
                </Link>
            </div>

            <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
                {items.length === 0 ? (
                    <div className="p-12 text-center" data-testid="imports-empty">
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
                        <div className="mt-3 text-base text-gray-900 font-medium">No imports yet</div>
                        <Link to="/imports/new" className="text-sm text-primary mt-2 inline-block">Run your first import →</Link>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50/70 text-left">
                                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                    <th className="px-4 py-3 font-medium">File</th>
                                    <th className="px-4 py-3 font-medium">Uploaded By</th>
                                    <th className="px-4 py-3 font-medium">Total</th>
                                    <th className="px-4 py-3 font-medium">Imported</th>
                                    <th className="px-4 py-3 font-medium">Duplicates</th>
                                    <th className="px-4 py-3 font-medium" title="Phone provided but couldn't normalize to E.164">Bad Phone</th>
                                    <th className="px-4 py-3 font-medium">Invalid</th>
                                    <th className="px-4 py-3 font-medium">Failed</th>
                                    <th className="px-4 py-3 font-medium">When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(i => (
                                    <tr key={i.id} className="border-t border-border hover:bg-gray-50/60 cursor-pointer" onClick={() => navigate(`/imports/${i.id}`)} data-testid={`import-row-${i.id}`}>
                                        <td className="px-4 py-3 text-gray-900 font-medium">
                                            <Link to={`/imports/${i.id}`} className="hover:underline" data-testid={`open-import-${i.id}`}>{i.filename}</Link>
                                            {!i.row_results_saved && i.status === "completed" && <div className="text-[11px] text-muted-foreground font-normal">No row details saved</div>}
                                            {["cancelled", "expired"].includes(i.status) && <div className="text-[11px] text-rose-700 font-normal" data-testid={`import-status-${i.id}`}>{i.status === "cancelled" ? "Cancelled" : "Expired"}: rows deleted</div>}
                                            {["uploaded", "previewing"].includes(i.status) && <div className="text-[11px] text-amber-700 font-normal" data-testid={`import-status-${i.id}`}>Not imported, expires {new Date(new Date(i.created_at).getTime() + 864e5).toLocaleString()}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">{i.uploaded_by_name || "—"}</td>
                                        <td className="px-4 py-3 tabular">{i.total_rows}</td>
                                        <td className="px-4 py-3 tabular text-emerald-700">{i.imported_count}</td>
                                        <td className="px-4 py-3 tabular text-amber-700">{i.duplicate_count}</td>
                                        <td className="px-4 py-3 tabular text-rose-700" data-testid={`import-bad-phone-${i.id}`}>{i.invalid_phone_count ?? 0}</td>
                                        <td className="px-4 py-3 tabular text-orange-700">{i.invalid_count ?? 0}</td>
                                        <td className="px-4 py-3 tabular text-rose-700">{i.failed_count}</td>
                                        <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(i.created_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
